import React, { useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import { listSkus, skuBindingXcodes, SkuRow, updateSkuGlobal } from '../../api/material';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import { i18nText } from '../../utils/i18n';
import { toast } from '../../utils/toast';

interface Props {
    open: boolean;
    /** 目标材料 (给选中的 SKU 追加绑定到它名下) */
    xcode: string;
    onClose: () => void;
    /** 直接入库成功后回调 (走审批流时数据未变, 不触发) */
    onLinked?: () => void;
}

/**
 * 关联已有 SKU: 从 SKU 库搜索并给选中 SKU 追加一条到本材料的绑定 (多对多, 2026-08-24).
 * 原有绑定保持不变 (追加而非改绑); 未绑定过的 SKU 本材料即成为主绑定.
 */
function LinkSkuModal({ open, xcode, onClose, onLinked }: Props) {
    const [input, setInput] = useState('');
    const [rows, setRows] = useState<SkuRow[]>([]);
    const [searching, setSearching] = useState(false);
    const [busy, setBusy] = useState(''); // 提交中的 skuCode
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setInput('');
        setRows([]);
        setError('');
        setBusy('');
    }, [open]);

    const search = async (kw: string) => {
        setSearching(true);
        setError('');
        try {
            const res = await listSkus({ keyword: kw || undefined, page: 1, pageSize: 20 });
            // 已绑定本材料的不出现在候选里
            setRows((res.list || []).filter((s) => !skuBindingXcodes(s).includes(xcode)));
        } catch (e) {
            setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        } finally {
            setSearching(false);
        }
    };

    const link = async (s: SkuRow) => {
        setBusy(s.skuCode);
        setError('');
        try {
            // 追加绑定: 保留原有绑定 (含主绑定顺序), 本材料排在末尾
            const res = await updateSkuGlobal(s.skuCode, { xcodes: [...skuBindingXcodes(s), xcode] });
            if (alertApprovalIfPending(res.message)) {
                onClose();
                return;
            }
            toast(`SKU ${s.skuCode} 已关联到 ${xcode}`);
            onLinked?.();
            onClose();
        } catch (e) {
            setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        } finally {
            setBusy('');
        }
    };

    return (
        <Modal open={open} onClose={onClose} title="关联已有 SKU" subtitle={`目标材料: ${xcode}`} width={640}>
            <div className="space-y-3">
                <form
                    className="flex gap-2"
                    onSubmit={(e) => {
                        e.preventDefault();
                        search(input.trim());
                    }}
                >
                    <input
                        className="inp flex-1"
                        placeholder="搜索 SKU 编码 / 产品名 (回车搜索)"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                    />
                    <button type="submit" className="btn-ghost" disabled={searching}>
                        {searching ? '搜索中…' : '搜索'}
                    </button>
                </form>

                {error && (
                    <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                        {error}
                    </div>
                )}

                {rows.length === 0 ? (
                    <div className="text-center text-xs text-slate-400 py-6">
                        {searching ? '搜索中…' : '输入关键词搜索 SKU 库; 已在本材料名下的不显示'}
                    </div>
                ) : (
                    <div className="max-h-[50vh] overflow-auto divide-y divide-slate-100 border border-slate-100 rounded-lg">
                        {rows.map((s) => (
                            <div key={s.id} className="flex items-center gap-3 px-3 py-2 text-xs">
                                <div className="min-w-0 flex-1">
                                    <div className="font-mono">{s.skuCode}</div>
                                    <div className="text-slate-500 truncate">
                                        {i18nText(s.productName || {}) || '—'}
                                    </div>
                                </div>
                                {(s.materials || []).length > 0 ? (
                                    <span
                                        className="tag bg-amber-50 text-amber-700 whitespace-nowrap"
                                        title={`已绑定: ${skuBindingXcodes(s).join('、')}; 关联为追加绑定, 原绑定不变`}
                                    >
                                        已绑 {s.materials.length} 个材料
                                    </span>
                                ) : (
                                    <span className="tag bg-slate-100 text-slate-500 whitespace-nowrap">
                                        未绑定
                                    </span>
                                )}
                                <button
                                    type="button"
                                    className="btn-ghost whitespace-nowrap"
                                    disabled={!!busy}
                                    onClick={() => link(s)}
                                >
                                    {busy === s.skuCode ? '关联中…' : '关联'}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    );
}

export default LinkSkuModal;
